import React, { useEffect, useState } from "react";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import Grid from "@mui/system/Grid";
import { BarChart } from "@mui/x-charts/BarChart";
import { isEmpty } from "lodash";
import { AxisConfig, ChartsXAxisProps } from "@mui/x-charts";

import {
  displayFlexEnd,
  flexAllCentre,
} from "../../../styles/mui/styles/display";
import fontWeight from "../../../styles/mui/fontWeight";
import {
  BarChartContainerStyle,
  BarLabelStyle,
  FaCircleXmarkIcon,
  ModalContainer,
  ModalHeaderBox,
  ModalTitle,
} from "../styles/style";
import { colors } from "../../../styles/colors";
import { CompareNPIDetails } from "../../../models/CompareNPI";
import { MyAccountModel } from "../../../models/MyAccount";
import { XAxisLabel } from "../../../constants/Scorecard";
import { Numbers } from "../../../constants/Referral";
import { sortedChartData } from "../../../utils";

export interface PropsFromState {
  isOpenModal: boolean;
  setIsOpenModal: (isOpen: boolean) => void;
  compareNPIData: CompareNPIDetails;
  loggedInUser: MyAccountModel;
}

export interface PropsFromDispatch {
  getCompareNPIDetails: (userId: string) => void;
}

export interface ChartData {
  label: string;
  value: number,
  npi: string
}

type AllProps = PropsFromState & PropsFromDispatch;

const CompareNpiModal: React.FC<AllProps> = ({
  isOpenModal,
  setIsOpenModal,
  getCompareNPIDetails,
  compareNPIData,
  loggedInUser,
}) => {
  const [chartData, setChartData] = useState<
    { label: string; value: number, npi: string}[]
  >([]);

  useEffect(() => {
    getCompareNPIDetails(loggedInUser.providerPortalUser.userId);
  }, []);

  useEffect(() => {
    updateChart(compareNPIData)
  }, [compareNPIData]);

  const updateChart = (compareNPIData: CompareNPIDetails) => {
    if (Array.isArray(compareNPIData)) {
      const formattedData = compareNPIData.map((item) => ({
        label: item.pcM_Prov_Name,
        value: updateValue(item),
        npi: item.pcM_NPI,
      }));
      setChartData(formattedData);
    }
  };

  const updateValue = (item: any) => {
    if (item.totalAverageScore) {
      return parseFloatValue(item)
    }
    return Numbers.ZERO;
  };

  const parseFloatValue = (item: any) => {
    return item.totalAverageScore === "0.0%"
      ? Numbers.ZERO
      : parseFloat(item.totalAverageScore.replace("%", ""));
  };

  const handleClose = () => {
    setIsOpenModal(false);
  };

  const currencyFormatter = (value: number) => {
    return `${value.toLocaleString()}%`;
  };

  const getValueWithLabel = (value: number) => {
    if (chartData !== undefined && !isEmpty(chartData)) {
      const compareNpi = chartData.filter((item) => Number(item.npi) === Number(value))[Numbers.ZERO]
      return `${compareNpi.label} : ${compareNpi.npi}`
    }
    return value;
  }

  const getChartDataLength = (chartData: ChartData[]) => {
    if (chartData) {
      return chartData.length > 0
    }
    return false;
  }
const data = sortedChartData(chartData)

  return (
    getChartDataLength(chartData) ? <Modal
      open={isOpenModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={flexAllCentre}
    >
      <Grid container sx={ModalContainer}>
        <Grid size={{ xs: 12 }}>
          <Grid container >
            <Grid size={{ xs: 11 }} sx={ModalHeaderBox}>
              <Typography
                variant="h5"
                color={colors.black[17]}
                fontWeight={fontWeight[6]}
                sx={ModalTitle}
              >
                Compare NPI Agencies
              </Typography>
            </Grid>
            <Grid size={{ xs: 1 }}>
              <Box sx={displayFlexEnd}>
                <IconButton onClick={handleClose}>
                  <FaCircleXmarkIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid container >
          <Grid size={{ xs: 12 }} sx={BarChartContainerStyle} mb={1}>
            <BarChart
              xAxis={[
                {
                  scaleType: 'band',
                  tickPlacement: "middle",
                  tickLabelPlacement: "middle",
                  data: data.map((item: ChartData) => `${item.npi}`),
                  label: XAxisLabel,
                  barGapRatio: 0.1,
                  categoryGapRatio: 0.6,
                  valueFormatter: (value, context) =>
                    context.location === 'tick'
                      ? value
                      : getValueWithLabel(value),
                } as AxisConfig<'band', any, ChartsXAxisProps>,
              ]}
              bottomAxis={{
                tickLabelStyle: {
                  fontSize: 10,
                },
              }}
              sx={BarLabelStyle}
              series={[
                {
                  data: data.map((item) => item.value),
                },
              ]}
              width={chartData && chartData.length > 5 ? 1200 : 850}
              height={400}
              barLabel={(item: any) => item.value === Numbers.ZERO ? "" : `${item.value.toFixed(2)}%`}
              yAxis={[
                {
                  max: 100,
                  disableLine: false,
                  valueFormatter: (value) => currencyFormatter(Number(value))
                },
              ]}
              colors={[colors.backgroundColor[17]]}
              grid={{ horizontal: true, vertical: false }}
            >
            </BarChart>
          </Grid>
        </Grid>
       
      </Grid>
    </Modal> : <></>
  );
};

export default CompareNpiModal;
